import React, { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import { useNavigate, useParams } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';
import LoadingSkeleton from '../components/LoadingSkeleton';

const EditGameList = () => {
    const { id } = useParams();
    const { isAuthenticated, user } = useContext(AuthContext);
    const navigate = useNavigate();

    const [loading, setLoading] = useState(true);
    const [formData, setFormData] = useState({
        title: '',
        description: '',
        isPublic: true,
        tags: ''
    });
    const [addedGames, setAddedGames] = useState([]);
    
    // Search state
    const [searchQuery, setSearchQuery] = useState('');
    const [searchResults, setSearchResults] = useState([]);
    const [searching, setSearching] = useState(false);
    const [showResults, setShowResults] = useState(false);

    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState('');

    useEffect(() => {
        fetchList();
    }, [id]);

    const fetchList = async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem('token');
            const config = token ? { headers: { 'Authorization': `Bearer ${token}` } } : {};
            
            const res = await axios.get(`/api/game-lists/${id}`, config);
            const list = res.data.data;

            // Check ownership
            if (user && list.owner && list.owner._id !== user._id) {
                navigate('/discover-lists');
                return;
            }

            setFormData({
                title: list.title,
                description: list.description || '',
                isPublic: list.isPublic,
                tags: list.tags ? list.tags.join(', ') : ''
            });

            setAddedGames(list.games.map(item => ({
                game: item.game,
                note: item.note || ''
            })));

            setError('');
        } catch (err) {
            console.error(err);
            setError('Failed to load list details');
        } finally {
            setLoading(false);
        }
    };

    const handleSearch = async (e) => {
        e.preventDefault();
        if (!searchQuery.trim()) return;

        setSearching(true);
        try {
            const response = await axios.get(`/api/games/search-rawg?q=${encodeURIComponent(searchQuery)}`);
            const rawgResults = response.data.results || [];
            if (rawgResults.length > 0) {
                setSearchResults(rawgResults);
            } else {
                const dbRes = await axios.get(`/api/games/search?q=${encodeURIComponent(searchQuery)}`);
                setSearchResults(dbRes.data.games || []);
            }
            setShowResults(true);
        } catch (err) {
            try {
                const dbRes = await axios.get(`/api/games/search?q=${encodeURIComponent(searchQuery)}`);
                setSearchResults(dbRes.data.games || []);
                setShowResults(true);
            } catch (err2) {
                console.error('Search error:', err2);
            }
        } finally {
            setSearching(false);
        }
    };

    const addGame = async (rawgItem) => {
        try {
            const token = localStorage.getItem('token');
            const headers = token ? { 'Authorization': `Bearer ${token}` } : {};
            const res = await axios.post('/api/games', { name: rawgItem.name }, { headers });
            const dbGame = res.data.game || res.data.data || res.data;
            if (!dbGame || !dbGame._id) return;
            if (addedGames.find(g => String(g.game._id) === String(dbGame._id))) return;
            setAddedGames([...addedGames, { game: dbGame, note: '' }]);
            setSearchQuery('');
            setShowResults(false);
        } catch (err) {
            console.error('Add game error:', err);
        }
    };

    const removeGame = (index) => {
        const newGames = [...addedGames];
        newGames.splice(index, 1);
        setAddedGames(newGames);
    };

    const updateGameNote = (index, note) => {
        const newGames = [...addedGames];
        newGames[index].note = note;
        setAddedGames(newGames);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!formData.title.trim()) {
            setError('Title is required');
            return;
        }

        setSubmitting(true);
        setError('');

        try {
            const token = localStorage.getItem('token');
            const listData = {
                title: formData.title,
                description: formData.description,
                isPublic: formData.isPublic,
                tags: formData.tags.split(',').map(tag => tag.trim()).filter(tag => tag),
                games: addedGames.map(item => ({
                    game: item.game._id,
                    note: item.note
                }))
            };

            const response = await axios.put(`/api/game-lists/${id}`, listData, {
                headers: { 'Authorization': `Bearer ${token}` }
            });

            if (response.data.success) {
                navigate(`/lists/${response.data.data._id}`);
            }
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to update list');
            setSubmitting(false);
        }
    };

    if (loading) return <LoadingSkeleton />;

    return (
        <div className="container" style={{ padding: '20px', maxWidth: '800px', margin: '0 auto' }}>
            <h1 style={{ marginBottom: '30px', color: 'var(--text-primary)' }}>Edit List</h1>
            
            {error && <div style={{ color: 'var(--error-text)', marginBottom: '20px', background: 'rgba(255,0,0,0.1)', padding: '10px', borderRadius: '8px' }}>{error}</div>}

            <form onSubmit={handleSubmit}>
                <div style={{ marginBottom: '20px' }}>
                    <label style={{ display: 'block', marginBottom: '8px', color: 'var(--text-secondary)' }}>List Title *</label>
                    <input
                        type="text"
                        value={formData.title}
                        onChange={(e) => setFormData({...formData, title: e.target.value})}
                        style={{
                            width: '100%',
                            padding: '12px',
                            borderRadius: '8px',
                            border: '1px solid var(--border-color)',
                            background: 'var(--card-bg)',
                            color: 'var(--text-primary)',
                            fontSize: '1.1rem'
                        }}
                        placeholder="e.g. Best RPGs of 2024"
                        required
                    />
                </div>

                <div style={{ marginBottom: '20px' }}>
                    <label style={{ display: 'block', marginBottom: '8px', color: 'var(--text-secondary)' }}>Description</label>
                    <textarea
                        value={formData.description}
                        onChange={(e) => setFormData({...formData, description: e.target.value})}
                        style={{
                            width: '100%',
                            padding: '12px',
                            borderRadius: '8px',
                            border: '1px solid var(--border-color)',
                            background: 'var(--card-bg)',
                            color: 'var(--text-primary)',
                            minHeight: '100px',
                            resize: 'vertical'
                        }}
                        placeholder="What is this list about?"
                    />
                </div>

                <div style={{ marginBottom: '20px' }}>
                    <label style={{ display: 'block', marginBottom: '8px', color: 'var(--text-secondary)' }}>Tags (comma separated)</label>
                    <input
                        type="text"
                        value={formData.tags}
                        onChange={(e) => setFormData({...formData, tags: e.target.value})}
                        style={{
                            width: '100%',
                            padding: '12px',
                            borderRadius: '8px',
                            border: '1px solid var(--border-color)',
                            background: 'var(--card-bg)',
                            color: 'var(--text-primary)'
                        }}
                        placeholder="rpg, indie, cozy"
                    />
                </div>

                <div style={{ marginBottom: '20px' }}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer', color: 'var(--text-primary)' }}>
                        <input
                            type="checkbox"
                            checked={formData.isPublic}
                            onChange={(e) => setFormData({...formData, isPublic: e.target.checked})}
                            style={{ width: '20px', height: '20px' }}
                        />
                        Make this list public
                    </label>
                </div>

                <div style={{ marginBottom: '30px', borderTop: '1px solid var(--border-color)', paddingTop: '20px' }}>
                    <h3 style={{ color: 'var(--text-primary)', marginBottom: '15px' }}>Games ({addedGames.length})</h3>
                    
                    <div style={{ marginBottom: '20px', position: 'relative' }}>
                        <div style={{ display: 'flex', gap: '10px' }}>
                            <input
                                type="text"
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                placeholder="Search for games to add..."
                                style={{
                                    flex: 1,
                                    padding: '12px',
                                    borderRadius: '8px',
                                    border: '1px solid var(--border-color)',
                                    background: 'var(--card-bg)',
                                    color: 'var(--text-primary)'
                                }}
                            />
                            <button
                                onClick={handleSearch}
                                type="button"
                                disabled={searching}
                                style={{
                                    padding: '0 20px',
                                    borderRadius: '8px',
                                    background: 'var(--bg-secondary)',
                                    color: 'var(--text-primary)',
                                    border: '1px solid var(--border-color)',
                                    cursor: 'pointer'
                                }}
                            >
                                {searching ? 'Searching...' : 'Search'}
                            </button>
                        </div>

                        {showResults && searchResults.length > 0 && (
                            <div style={{
                                position: 'absolute',
                                top: '100%',
                                left: 0,
                                right: 0,
                                background: 'var(--card-bg)',
                                border: '1px solid var(--border-color)',
                                borderRadius: '8px',
                                marginTop: '5px',
                                maxHeight: '300px',
                                overflowY: 'auto',
                                zIndex: 10,
                                boxShadow: '0 4px 12px rgba(0,0,0,0.5)'
                            }}>
                                {searchResults.map(game => (
                                    <div
                                        key={game.rawgId || game._id}
                                        onClick={() => addGame(game)}
                                        style={{
                                            padding: '10px',
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '10px',
                                            cursor: 'pointer',
                                            borderBottom: '1px solid var(--border-color)'
                                        }}
                                        onMouseEnter={(e) => e.currentTarget.style.background = 'var(--bg-secondary)'}
                                        onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                                    >
                                        <img 
                                            src={game.coverImage || '/placeholder-game.jpg'} 
                                            alt={game.name} 
                                            style={{ width: '40px', height: '50px', objectFit: 'cover', borderRadius: '4px' }}
                                        />
                                        <div>
                                            <div style={{ color: 'var(--text-primary)', fontWeight: 'bold' }}>{game.name}</div>
                                            <div style={{ color: 'var(--text-secondary)', fontSize: '0.8rem' }}>{game.released || game.releaseYear || ''}</div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>

                    <div style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
                        {addedGames.map((item, index) => (
                            <div key={index} style={{
                                background: 'var(--bg-secondary)',
                                padding: '15px',
                                borderRadius: '8px',
                                display: 'flex',
                                gap: '15px',
                                alignItems: 'flex-start'
                            }}>
                                <div style={{ 
                                    width: '30px', 
                                    height: '30px', 
                                    borderRadius: '50%', 
                                    background: 'var(--primary-color)', 
                                    display: 'flex', 
                                    alignItems: 'center', 
                                    justifyContent: 'center',
                                    color: 'white',
                                    fontWeight: 'bold',
                                    flexShrink: 0
                                }}>
                                    {index + 1}
                                </div>
                                <img 
                                    src={item.game.coverImage || '/placeholder-game.jpg'} 
                                    alt={item.game.name} 
                                    style={{ width: '60px', height: '80px', objectFit: 'cover', borderRadius: '4px', flexShrink: 0 }}
                                />
                                <div style={{ flex: 1 }}>
                                    <h4 style={{ margin: '0 0 10px 0', color: 'var(--text-primary)' }}>{item.game.name}</h4>
                                    <textarea
                                        value={item.note}
                                        onChange={(e) => updateGameNote(index, e.target.value)}
                                        placeholder="Add a note about this game..."
                                        style={{
                                            width: '100%',
                                            padding: '8px',
                                            borderRadius: '6px',
                                            border: '1px solid var(--border-color)',
                                            background: 'var(--card-bg)',
                                            color: 'var(--text-primary)',
                                            fontSize: '0.9rem',
                                            resize: 'vertical',
                                            minHeight: '60px'
                                        }}
                                    />
                                </div>
                                <button
                                    type="button"
                                    onClick={() => removeGame(index)}
                                    style={{
                                        background: 'transparent',
                                        border: 'none',
                                        color: 'var(--error-text)',
                                        cursor: 'pointer',
                                        fontSize: '1.2rem'
                                    }}
                                >
                                    ✕
                                </button>
                            </div>
                        ))}
                    </div>
                </div>

                <div style={{ display: 'flex', gap: '15px' }}>
                    <button
                        type="submit"
                        disabled={submitting}
                        style={{
                            flex: 1,
                            padding: '15px',
                            background: 'var(--primary-color)',
                            color: 'white',
                            border: 'none',
                            borderRadius: '8px',
                            fontSize: '1.1rem',
                            fontWeight: 'bold',
                            cursor: submitting ? 'not-allowed' : 'pointer',
                            opacity: submitting ? 0.7 : 1
                        }}
                    >
                        {submitting ? 'Saving...' : 'Save Changes'}
                    </button>
                    <button
                        type="button"
                        onClick={() => navigate(`/lists/${id}`)}
                        style={{
                            padding: '15px 30px',
                            background: 'var(--bg-secondary)',
                            color: 'var(--text-primary)',
                            border: '1px solid var(--border-color)',
                            borderRadius: '8px',
                            fontSize: '1.1rem',
                            fontWeight: 'bold',
                            cursor: 'pointer'
                        }}
                    >
                        Cancel
                    </button>
                </div>
            </form>
        </div>
    );
};

export default EditGameList;